'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Bot, Sparkles, Shield, TrendingUp, Users, Brain, Copy, Check, ArrowRight, ChevronRight } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
  route: string;
  color: string;
}

const demoCredentials: DemoCredential[] = [
  { role: 'Employee', email: 'priya.mehta@ecity.ai', password: 'Employee@2026', route: '/employee-dashboard', color: 'bg-primary/10 text-primary' },
  { role: 'Manager', email: 'kiran.rao@ecity.ai', password: 'Manager@2026', route: '/manager-dashboard', color: 'bg-info/10 text-info' },
  { role: 'HR Manager', email: 'sunita.iyer@ecity.ai', password: 'HRAdmin@2026', route: '/hr-dashboard', color: 'bg-success/10 text-success' },
  { role: 'Executive', email: 'rajiv.nair@ecity.ai', password: 'Exec@2026', route: '/executive-command-center', color: 'bg-warning/10 text-warning' },
];

const routeByEmail: Record<string, string> = {
  'priya.mehta@ecity.ai': '/employee-dashboard',
  'kiran.rao@ecity.ai': '/manager-dashboard',
  'sunita.iyer@ecity.ai': '/hr-dashboard',
  'rajiv.nair@ecity.ai': '/executive-command-center',
};

export default function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({ defaultValues: { remember: false } });

  const onSubmit = (data: LoginFormValues) => {
    setLoading(true);
    // Backend integration point: Supabase Auth signInWithPassword
    setTimeout(() => {
      const validEmails = demoCredentials.map((d) => d.email);
      if (!validEmails.includes(data.email)) {
        toast.error('Invalid credentials — use the demo accounts below to sign in');
        setLoading(false);
        return;
      }
      const route = routeByEmail[data.email] || '/employee-dashboard';
      toast.success('Signed in successfully!');
      router.push(route);
    }, 1000);
  };

  const autofill = (cred: DemoCredential) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col w-[52%] bg-primary relative overflow-hidden p-12">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute top-1/2 -right-40 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute bottom-20 left-20 w-60 h-60 rounded-full bg-white/5" />
        </div>

        <div className="relative z-10 flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <AppLogo size={40} />
            <div>
              <span className="text-white font-bold text-xl leading-none block">HumanOS AI</span>
              <span className="text-white/60 text-xs">eCity For IT Services</span>
            </div>
          </div>

          {/* Hero */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="mb-3">
              <span className="inline-flex items-center gap-1.5 bg-white/10 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                <Sparkles size={12} />
                AI-Native Workforce OS
              </span>
            </div>
            <h1 className="text-4xl font-bold text-white leading-tight mb-4">
              Your team,<br />self-managed by AI.
            </h1>
            <p className="text-white/70 text-lg leading-relaxed max-w-sm">
              AI coaches employees, proposes KPIs, monitors performance, and surfaces risks — so managers intervene only when it matters.
            </p>

            {/* Feature pills */}
            <div className="grid grid-cols-2 gap-3 mt-8">
              {[
                { icon: <Bot size={16} />, label: 'AI KPI Engine', desc: 'Proposes KPIs from JDs' },
                { icon: <Brain size={16} />, label: 'Burnout Detection', desc: 'Real-time risk alerts' },
                { icon: <TrendingUp size={16} />, label: 'Self-Management', desc: 'Composite score engine' },
                { icon: <Users size={16} />, label: 'Team Autonomy', desc: 'Minimal manager dependency' },
              ].map((f) => (
                <div key={`feat-${f.label}`} className="bg-white/10 rounded-xl p-3">
                  <div className="text-white mb-1">{f.icon}</div>
                  <p className="text-white text-sm font-semibold">{f.label}</p>
                  <p className="text-white/60 text-xs">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-6">
            {[
              { value: '94%', label: 'KPI Accuracy' },
              { value: '78%', label: 'Team Autonomy' },
              { value: '3.2x', label: 'Faster Reviews' },
            ].map((s) => (
              <div key={`stat-${s.label}`}>
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-white/60 text-xs">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="font-bold text-foreground text-lg">HumanOS AI</span>
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
          <p className="text-muted-foreground text-sm mb-8">Sign in to your workforce portal</p>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Work Email
              </label>
              <input
                type="email"
                placeholder="you@ecity.ai"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
                className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all"
              />
              {errors.email && (
                <p className="text-xs text-danger mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-danger mt-1">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('remember')}
                  className="w-4 h-4 rounded border-border accent-primary"
                />
                <span className="text-sm text-muted-foreground">Remember me</span>
              </label>
              <a href="#" className="text-sm text-primary hover:underline font-medium">
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground rounded-xl py-2.5 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ minHeight: '42px' }}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign In to HumanOS AI
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-8 p-4 bg-card border border-border rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={14} className="text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Demo Accounts — Click to autofill</span>
            </div>
            <div className="space-y-2">
              {demoCredentials.map((cred) => (
                <div
                  key={`cred-${cred.role}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer transition-colors border border-transparent hover:border-border"
                  onClick={() => autofill(cred)}
                >
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${cred.color}`}>
                    {cred.role}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground font-medium truncate">{cred.email}</p>
                    <p className="text-xs text-muted-foreground">{cred.password}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(cred.email, `email-${cred.role}`); }}
                      className="p-1 rounded hover:bg-muted-foreground/10 transition-colors"
                      title="Copy email"
                    >
                      {copiedField === `email-${cred.role}` ? <Check size={12} className="text-success" /> : <Copy size={12} className="text-muted-foreground" />}
                    </button>
                    <ChevronRight size={14} className="text-muted-foreground self-center" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            By signing in, you agree to our{' '}
            <a href="#" className="text-primary hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-primary hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}